/*!
 * Copyright (c) HSO.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node, IModuleProps, Drawer } from '@msdyn365-commerce-modules/utilities';
import { reaction } from 'mobx';
import * as React from 'react';
import {
    IProductSpecificationExtentedProps,
    IProductSpecificationViewProps,
    IProductSpecificationState,
    IProductSpecificationData,
    ProductSpecificationTableRow,
    AttributesForSelectedVariantInput,
    getAttributesForSelectedVariant
} from '@msdyn365-commerce-modules/product-specification';
import { IProductSpecificationProps } from '../definition-extensions/product-specification.ext.props.autogenerated';
import { AttributeDefinition, AttributeValue } from '@msdyn365-commerce/retail-proxy';
import * as Msdyn365 from '@msdyn365-commerce/core';

export class ProductSpecificationView extends React.PureComponent<IProductSpecificationExtentedProps & IProductSpecificationProps<{}>, IProductSpecificationState> {
    public constructor(props: IProductSpecificationExtentedProps & IProductSpecificationProps<{}>) {
        super(props);
        this.state = {
            isDrawerOpen: false,
            drawerListId: undefined,
            isAdditionalMediaDrawerOpen: false
        };
    }

    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;
        const catalogId = Msdyn365.getCatalogId(this.props.context.request);
        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        data.product.result.RecordId,

                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result,
                        catalogId
                    );

                    getAttributesForSelectedVariant(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: IProductSpecificationExtentedProps, nextState: IProductSpecificationState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { ProductSpecification, ProductSpecificationTableProps, ProductSpecificationTableBodyProps, title, isAccrodion } = (this.props as unknown) as IProductSpecificationViewProps;

        let data: IProductSpecificationData;
        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }

        const filterProductSpecificationResult = this._filterProductSpecificationResult(data, isAccrodion);
        const rowdata =
            filterProductSpecificationResult &&
            ArrayExtensions.hasElements(filterProductSpecificationResult) &&
            filterProductSpecificationResult.filter(row => !ObjectExtensions.isNullOrUndefined(row));

        return rowdata && ArrayExtensions.hasElements(rowdata) ? (
            <Module {...ProductSpecification}>
                {title}
                <Node {...ProductSpecificationTableProps}>
                    <Node {...ProductSpecificationTableBodyProps}>{rowdata}</Node>
                </Node>
            </Module>
        ) : null;
    }

    /**
     * Handle Heading Text Change.
     * @param event - Current event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    private _filterProductSpecificationResult(
        content: IProductSpecificationData,
        isAccrodion: boolean | undefined
    ): (JSX.Element | null)[] {
        return isAccrodion ? content && this._createDrawerBody(content) : content && this._createTableBody(content);
    }

    private _createTableBody(content: IProductSpecificationData): (JSX.Element | null)[] {
        return content.productSpecificationData.result!.map((product, index) => {
            const cellData: JSX.Element | null = this._renderProductCell(product);

            if (!cellData) {
                return null;
            }

            return (
                <ProductSpecificationTableRow
                    className='ms-product-specification__table-row'
                    key={index}
                    productName={product.Name}
                    cellData={cellData}
                />
            );
        });
    }

    private _createDrawerBody(content: IProductSpecificationData): (JSX.Element | null)[] {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };
        return content.productSpecificationData.result!.map(product => {
            const cellData: JSX.Element | null = this._renderProductCell(product);
            if (!cellData) {
                return null;
            }
            return (
                <Module {...accordionItemContainer} key={product.RecordId}>
                    <Drawer
                        key={product.RecordId}
                        collapseProps={{
                            timeout: 0,
                            isOpen: this.state.isDrawerOpen && this.state.drawerListId === product.RecordId
                        }}
                        className='ms-product-specification__drawer'
                        openGlyph='ms-product-specification__drawer-open'
                        closeGlyph='ms-product-specification__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={product.Name ?? ''}
                        onToggle={this._toggle(product.RecordId)}
                    >
                        <div className='ms-product-details'>{cellData}</div>
                    </Drawer>
                </Module>
            );
        });
    }

    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };

    private _renderProductCell(product: AttributeValue): JSX.Element | null {
        if (this._isAttributeValueHidden(product)) {
            return null;
        }

        switch (product.DataTypeValue) {
            case 0: // None
                // Return span here instead of null so the row still shows
                return <span />;
            case 1: // Currency
                return this._renderCurrencyCell(product);
            case 2: // DateTime
                return this._renderDateTimeCell(product);
            case 3: // Decimal
                return this._renderDecimalCell(product);
            case 4: // Integer
                return this._renderIntegerCell(product);
            case 5: // Text
                return this._renderTextCell(product);
            case 6: // TrueFalse
                return this._renderTrueFalseCell(product);
            case 40: // Video
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case 41: // Image
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
                return null;
        }
    }

    private _isAttributeValueHidden(product: AttributeValue): boolean {
        let documentAttribute = (product && this.props.config.hideProductDocument && product.Name?.trim().toUpperCase().includes('document'.trim().toUpperCase())) || false;
        let descriptionAttribute = (product && this.props.config.hideProductDescription && product.Name?.trim().toUpperCase().includes('description'.trim().toUpperCase())) || false;
        let searchAttribute = (product && this.props.config.hideSearchKeyword && product.Name?.trim().toUpperCase().includes('search'.trim().toUpperCase())) || false;

        if (documentAttribute || descriptionAttribute || searchAttribute) {
            return true;
        }

        let extProp: string =
            (product &&
                product.ExtensionProperties &&
                product.ExtensionProperties.some(prop => {
                    return prop.Key?.startsWith('AttributeDefinition');
                }) &&
                ArrayExtensions.firstOrDefault(product.ExtensionProperties.filter(prop => {
                    return prop.Key?.startsWith('AttributeDefinition');
                }))?.Value?.StringValue) ||
            '';

        if (extProp != null && extProp != undefined && extProp != '') {
            let prop: AttributeDefinition = JSON.parse(extProp);
            return (this.props.config.hideHiddenAttribute && prop && prop.IsHidden) || false;
        }

        return false;
    }

    private _renderCurrencyCell(product: AttributeValue): JSX.Element | null {
        if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
            return (
                <span>
                    {product.CurrencyCode}
                    {product.CurrencyValue}
                </span>
            );
        }

        return null;
    }

    private _renderDateTimeCell(product: AttributeValue): JSX.Element | null {
        if (product.DateTimeOffsetValue?.toUTCString) {
            return <span>{product.DateTimeOffsetValue.toUTCString()}</span>;
        }

        return null;
    }

    private _renderIntegerCell(product: AttributeValue): JSX.Element | null {
        if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
            return <span>{product.IntegerValue}</span>;
        }

        return null;
    }

    private _renderDecimalCell(product: AttributeValue): JSX.Element | null {
        if (product.FloatValue !== null && product.FloatValue !== undefined) {
            return <span>{product.FloatValue}</span>;
        }

        return null;
    }

    private _renderTextCell(product: AttributeValue): JSX.Element | null {
        if (product.TextValue !== null && product.TextValue !== undefined) {
            return <span dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(product.TextValue)} />;
        }

        return null;
    }

    private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
        if (product.BooleanValue) {
            return <span>{this.props.resources.trueValueText ? this.props.resources.trueValueText : 'Yes'}</span>;
        }

        return <span>{this.props.resources.falseValueText ? this.props.resources.falseValueText : 'No'}</span>;
    }
}

export default ProductSpecificationView;
